import React, { useState, useEffect, useCallback } from 'react';
import {
  CheckCircle, Clock, AlertTriangle, Loader2, Search, X, ChevronRight,
  Globe2, User, Filter, RefreshCw, ExternalLink, Save, Lock, UserPlus,
  Timer, TrendingUp, ShieldAlert, CalendarClock
} from 'lucide-react';
import { cxDashboardAPI, settingsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { saveFilters, loadFilters, clearPageFilters } from '../utils/filterPersistence';
import { CXLeaderApprovalInsights } from '../components/DashboardApprovalInsights';

const STATUS_STYLES = {
  completed: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  in_progress: 'bg-amber-100 text-amber-700 border-amber-200',
  assigned: 'bg-blue-100 text-blue-700 border-blue-200',
  pending_assignment: 'bg-red-100 text-red-700 border-red-200',
  not_started: 'bg-gray-100 text-gray-600 border-gray-200',
};

const STATUS_LABELS = {
  completed: 'Completed', in_progress: 'In Progress', assigned: 'Assigned',
  pending_assignment: 'Needs Assignment', not_started: 'Not Started',
};

const SLA_BADGE = {
  on_time: { label: 'On Time', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  late: { label: 'Late', cls: 'bg-red-100 text-red-700 border-red-200' },
  overdue: { label: 'Overdue', cls: 'bg-red-100 text-red-700 border-red-200' },
  on_track: { label: 'On Track', cls: 'bg-blue-100 text-blue-700 border-blue-200' },
};

const OTA_PLATFORMS = ['Airbnb', 'Agoda', 'Expedia', 'MMT', 'Goibibo', 'Hotels_Com', 'Booking_Com', 'Trip_com', 'Priceline', 'Tripadvisor', 'EasemyTrip', 'Cleartrip'];

const TIME_PRESETS = [
  { label: 'All Time', key: 'all' },
  { label: 'Last 7 days', key: '7d' },
  { label: 'This Month', key: 'this_month' },
  { label: 'Custom', key: 'custom' },
];

function getDateRange(key) {
  const now = new Date();
  const fmt = d => d.toISOString().split('T')[0];
  if (key === '7d') { const f = new Date(now); f.setDate(now.getDate() - 7); return { date_from: fmt(f), date_to: fmt(now) }; }
  if (key === 'this_month') { return { date_from: fmt(new Date(now.getFullYear(), now.getMonth(), 1)), date_to: fmt(now) }; }
  return {};
}

function fmtDate(iso) {
  if (!iso) return '-';
  try {
    return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
}

// --- SLA Badge Component ---
const SlaBadge = ({ status }) => {
  const info = SLA_BADGE[status];
  if (!info) return null;
  return <span className={`text-[10px] px-2 py-0.5 rounded-full border font-medium ${info.cls}`}>{info.label}</span>;
};

// --- OTA Drawer ---
const OTADrawer = ({ lead, onClose, onAssign, onComplete, users }) => {
  const [editingOTA, setEditingOTA] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [assignUser, setAssignUser] = useState({});
  const [assignDeadline, setAssignDeadline] = useState({});
  const [assigning, setAssigning] = useState({});

  if (!lead) return null;

  const handleAssign = async (otaIdx) => {
    const email = assignUser[otaIdx];
    if (!email) return;
    setAssigning(p => ({ ...p, [otaIdx]: true }));
    try {
      const deadline = assignDeadline[otaIdx] || '';
      await onAssign(lead.id, otaIdx, email, deadline);
      setAssignUser(p => ({ ...p, [otaIdx]: '' }));
      setAssignDeadline(p => ({ ...p, [otaIdx]: '' }));
    } finally { setAssigning(p => ({ ...p, [otaIdx]: false })); }
  };

  const startEdit = (ota) => {
    setEditingOTA(ota.index);
    setEditForm({ hotel_id: ota.hotel_id, listing_url: ota.listing_url, property_name_on_ota: ota.property_name_on_ota, access_to_hotel: ota.access_to_hotel });
  };

  const handleSave = async (otaIdx, markComplete = false) => {
    setSaving(true);
    try {
      await onComplete(lead.id, otaIdx, { ...editForm, mark_completed: markComplete });
      setEditingOTA(null);
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="ota-drawer">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative w-full max-w-2xl bg-white shadow-2xl flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 shrink-0">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-bold text-gray-900 text-lg">{lead.hotel_name}</h3>
              <p className="text-xs text-gray-500 mt-0.5">Lead: {lead.id} | Owner: {lead.lead_owner} | Date: {lead.lead_date}</p>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-gray-200 rounded-lg" data-testid="close-drawer-btn"><X size={20} /></button>
          </div>
          <div className="flex gap-4 mt-3 text-xs">
            <span className="px-2 py-1 bg-teal-50 text-teal-700 rounded-md font-medium">{lead.ota_count} OTAs</span>
            <span className="px-2 py-1 bg-emerald-50 text-emerald-700 rounded-md font-medium">{lead.ota_completed} Done</span>
            <span className="px-2 py-1 bg-red-50 text-red-700 rounded-md font-medium">{lead.ota_pending} Incomplete</span>
          </div>
        </div>

        {/* OTA Rows */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {lead.ota_info && lead.ota_info.length > 0 ? lead.ota_info.map(ota => {
            const isLocked = ota.assignment_status === 'completed' || ota.locked;
            const isEditing = editingOTA === ota.index;
            return (
              <div key={ota.index} className={`border rounded-xl overflow-hidden ${isLocked ? 'border-emerald-200 bg-emerald-50/30' : 'border-gray-200'}`} data-testid={`ota-row-${ota.index}`}>
                <div className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Globe2 size={18} className="text-teal-600" />
                      <span className="font-bold text-gray-800">{ota.ota_name}</span>
                      {isLocked && <Lock size={14} className="text-emerald-500" />}
                    </div>
                    <div className="flex items-center gap-2">
                      <SlaBadge status={ota.sla_status} />
                      <span className={`text-xs px-2.5 py-1 rounded-full border font-medium ${STATUS_STYLES[ota.assignment_status]}`}>
                        {STATUS_LABELS[ota.assignment_status]}
                      </span>
                    </div>
                  </div>

                  {/* Time Tracking Info */}
                  {(ota.time_taken_hours != null || ota.deadline_at || ota.assigned_at) && (
                    <div className="mb-3 flex flex-wrap gap-3 text-xs text-gray-500">
                      {ota.assigned_at && <span className="flex items-center gap-1"><CalendarClock size={12} /> Assigned: {fmtDate(ota.assigned_at)}</span>}
                      {ota.deadline_at && <span className={`flex items-center gap-1 ${ota.sla_status === 'overdue' ? 'text-red-600 font-medium' : ''}`}><Clock size={12} /> Deadline: {fmtDate(ota.deadline_at)}</span>}
                      {ota.completed_at && <span className="flex items-center gap-1"><CheckCircle size={12} /> Completed: {fmtDate(ota.completed_at)}</span>}
                      {ota.time_taken_hours != null && <span className="flex items-center gap-1 font-medium text-gray-700"><Timer size={12} /> {ota.time_taken_hours}h taken</span>}
                    </div>
                  )}

                  {/* Current Data */}
                  <div className="grid grid-cols-2 gap-3 text-sm mb-3">
                    <div><span className="text-xs text-gray-500 block">Hotel ID</span><span className={`font-medium ${ota.hotel_id ? 'text-gray-800' : 'text-red-400 italic'}`}>{ota.hotel_id || 'Missing'}</span></div>
                    <div><span className="text-xs text-gray-500 block">Listing URL</span>
                      {ota.listing_url ? <a href={ota.listing_url} target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:underline text-xs flex items-center gap-1 truncate"><ExternalLink size={10} />{ota.listing_url}</a> : <span className="text-red-400 italic font-medium">Missing</span>}
                    </div>
                    <div><span className="text-xs text-gray-500 block">Assigned To</span><span className="font-medium text-gray-700">{ota.assigned_to || 'Unassigned'}</span></div>
                    {ota.assigned_by && <div><span className="text-xs text-gray-500 block">Assigned By</span><span className="font-medium text-gray-700">{ota.assigned_by}</span></div>}
                    <div><span className="text-xs text-gray-500 block">Access</span><span className="font-medium text-gray-700">{ota.access_to_hotel || '-'}</span></div>
                  </div>

                  {/* Assignment (only for incomplete, non-locked) */}
                  {!isLocked && ota.is_incomplete && (
                    <div className="p-3 bg-gray-50 rounded-lg border border-gray-100 mb-3 space-y-2" data-testid={`assign-section-${ota.index}`}>
                      <div className="flex items-center gap-2">
                        <UserPlus size={16} className="text-gray-400 shrink-0" />
                        <select value={assignUser[ota.index] || ''} onChange={e => setAssignUser(p => ({ ...p, [ota.index]: e.target.value }))}
                          className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white" data-testid={`assign-select-${ota.index}`}>
                          <option value="">Assign to...</option>
                          {(users || []).map(u => <option key={u.email} value={u.name || u.email}>{u.name || u.email}</option>)}
                        </select>
                      </div>
                      <div className="flex items-center gap-2">
                        <Clock size={16} className="text-gray-400 shrink-0" />
                        <input type="datetime-local" value={assignDeadline[ota.index] || ''}
                          onChange={e => setAssignDeadline(p => ({ ...p, [ota.index]: e.target.value }))}
                          className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm" placeholder="Set deadline"
                          data-testid={`assign-deadline-${ota.index}`} />
                      </div>
                      <button onClick={() => handleAssign(ota.index)} disabled={!assignUser[ota.index] || assigning[ota.index]}
                        className="w-full px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 disabled:opacity-50" data-testid={`assign-btn-${ota.index}`}>
                        {assigning[ota.index] ? <Loader2 size={14} className="animate-spin inline mr-1" /> : null} Assign with Deadline
                      </button>
                    </div>
                  )}

                  {/* Edit Form (for assigned/in_progress, non-locked) */}
                  {!isLocked && isEditing && (
                    <div className="p-3 bg-blue-50 rounded-lg border border-blue-100 space-y-3" data-testid={`edit-form-${ota.index}`}>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="text-xs font-medium text-gray-600 mb-1 block">Hotel ID</label>
                          <input type="text" value={editForm.hotel_id || ''} onChange={e => setEditForm(f => ({ ...f, hotel_id: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" data-testid={`edit-hotel-id-${ota.index}`} />
                        </div>
                        <div>
                          <label className="text-xs font-medium text-gray-600 mb-1 block">Listing URL</label>
                          <input type="text" value={editForm.listing_url || ''} onChange={e => setEditForm(f => ({ ...f, listing_url: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" data-testid={`edit-listing-url-${ota.index}`} />
                        </div>
                        <div>
                          <label className="text-xs font-medium text-gray-600 mb-1 block">Property Name on OTA</label>
                          <input type="text" value={editForm.property_name_on_ota || ''} onChange={e => setEditForm(f => ({ ...f, property_name_on_ota: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" />
                        </div>
                        <div>
                          <label className="text-xs font-medium text-gray-600 mb-1 block">Access to Hotel</label>
                          <select value={editForm.access_to_hotel || ''} onChange={e => setEditForm(f => ({ ...f, access_to_hotel: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white">
                            <option value="">Select...</option>
                            <option value="Granted">Granted</option>
                            <option value="Not Granted">Not Granted</option>
                          </select>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button onClick={() => handleSave(ota.index, false)} disabled={saving}
                          className="flex-1 flex items-center justify-center gap-1.5 px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 disabled:opacity-50" data-testid={`save-ota-${ota.index}`}>
                          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save
                        </button>
                        <button onClick={() => handleSave(ota.index, true)} disabled={saving || !editForm.hotel_id || !editForm.listing_url}
                          className="flex-1 flex items-center justify-center gap-1.5 px-4 py-2 bg-amber-600 text-white rounded-lg text-sm font-medium hover:bg-amber-700 disabled:opacity-50" data-testid={`complete-ota-${ota.index}`}>
                          {saving ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle size={14} />} Send to QC
                        </button>
                        <button onClick={() => setEditingOTA(null)} className="px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50">Cancel</button>
                      </div>
                    </div>
                  )}
                  {!isLocked && !isEditing && (ota.assigned_to || !ota.is_incomplete) && (
                    <button onClick={() => startEdit(ota)} className="text-xs text-teal-600 hover:text-teal-800 font-medium" data-testid={`edit-ota-btn-${ota.index}`}>Edit Fields</button>
                  )}
                </div>
              </div>
            );
          }) : (
            <div className="text-center py-12 text-gray-400">
              <Globe2 size={36} className="mx-auto mb-2 text-gray-300" />
              <p className="text-sm">No valid OTA entries for this lead</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// --- Main CX Leader Dashboard ---
const CXLeaderDashboard = () => {
  const { user } = useAuth();
  const [leads, setLeads] = useState([]);
  const [analytics, setAnalytics] = useState({});
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [search, setSearch] = useState('');
  const [selectedLead, setSelectedLead] = useState(null);
  const [users, setUsers] = useState([]);
  const [showFilters, setShowFilters] = useState(false);

  // Filters - restore from localStorage
  const _sf = loadFilters(user?.id, 'cx_dashboard');
  const [timePreset, setTimePreset] = useState(_sf?.timePreset || 'all');
  const [customFrom, setCustomFrom] = useState(_sf?.customFrom || '');
  const [customTo, setCustomTo] = useState(_sf?.customTo || '');
  const [userFilter, setUserFilter] = useState(_sf?.userFilter || '');
  const [otaStatusFilter, setOtaStatusFilter] = useState(_sf?.otaStatusFilter || '');
  const [otaPlatformFilter, setOtaPlatformFilter] = useState(_sf?.otaPlatformFilter || '');
  const [completenessFilter, setCompletenessFilter] = useState(_sf?.completenessFilter || '');
  // SLA Filters
  const [deadlineStatusFilter, setDeadlineStatusFilter] = useState(_sf?.deadlineStatusFilter || '');
  const [assignedFrom, setAssignedFrom] = useState(_sf?.assignedFrom || '');
  const [assignedTo, setAssignedTo] = useState(_sf?.assignedTo || '');
  const [completedFrom, setCompletedFrom] = useState(_sf?.completedFrom || '');
  const [completedTo, setCompletedTo] = useState(_sf?.completedTo || '');

  // Persist filters on change
  useEffect(() => {
    saveFilters(user?.id, 'cx_dashboard', { timePreset, customFrom, customTo, userFilter, otaStatusFilter, otaPlatformFilter, completenessFilter, deadlineStatusFilter, assignedFrom, assignedTo, completedFrom, completedTo });
  }, [timePreset, customFrom, customTo, userFilter, otaStatusFilter, otaPlatformFilter, completenessFilter, deadlineStatusFilter, assignedFrom, assignedTo, completedFrom, completedTo, user?.id]);

  useEffect(() => {
    (async () => {
      try {
        const res = await settingsAPI.getUsers();
        setUsers(Array.isArray(res.data) ? res.data : res.data?.users || []);
      } catch {}
    })();
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: pageSize, search };
      if (userFilter) params.user_filter = userFilter;
      if (otaStatusFilter) params.ota_status = otaStatusFilter;
      if (otaPlatformFilter) params.ota_platform = otaPlatformFilter;
      if (completenessFilter) params.completeness = completenessFilter;
      if (deadlineStatusFilter) params.deadline_status = deadlineStatusFilter;
      if (assignedFrom) params.assigned_from = assignedFrom;
      if (assignedTo) params.assigned_to_date = assignedTo;
      if (completedFrom) params.completed_from = completedFrom;
      if (completedTo) params.completed_to_date = completedTo;
      if (timePreset === 'custom') {
        if (customFrom) params.date_from = customFrom;
        if (customTo) params.date_to = customTo;
      } else if (timePreset !== 'all') {
        const range = getDateRange(timePreset);
        if (range.date_from) params.date_from = range.date_from;
        if (range.date_to) params.date_to = range.date_to;
      }
      const res = await cxDashboardAPI.getClosedWonLeads(params);
      setLeads(res.data.leads || []);
      setTotalPages(res.data.total_pages || 1);
      setTotalRecords(res.data.total || 0);
      setAnalytics(res.data.analytics || {});
    } catch (err) { console.error('CX Dashboard error:', err); }
    finally { setLoading(false); }
  }, [page, search, userFilter, otaStatusFilter, otaPlatformFilter, completenessFilter, timePreset, customFrom, customTo, deadlineStatusFilter, assignedFrom, assignedTo, completedFrom, completedTo, pageSize]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleAssign = async (leadId, otaIdx, assignTo, deadlineAt) => {
    const payload = { assigned_to: assignTo };
    if (deadlineAt) payload.deadline_at = deadlineAt;
    await cxDashboardAPI.assignOTA(leadId, otaIdx, payload);
    loadData();
    if (selectedLead?.id === leadId) {
      const res = await cxDashboardAPI.getClosedWonLeads({ search: leadId, limit: 1 });
      const updated = (res.data.leads || []).find(l => l.id === leadId);
      if (updated) setSelectedLead(updated);
    }
  };

  const handleComplete = async (leadId, otaIdx, data) => {
    await cxDashboardAPI.completeOTA(leadId, otaIdx, data);
    loadData();
    if (selectedLead?.id === leadId) {
      const res = await cxDashboardAPI.getClosedWonLeads({ search: leadId, limit: 1 });
      const updated = (res.data.leads || []).find(l => l.id === leadId);
      if (updated) setSelectedLead(updated);
    }
  };

  const a = analytics;
  const hasActiveFilters = userFilter || otaStatusFilter || otaPlatformFilter || completenessFilter || timePreset !== 'all' || deadlineStatusFilter || assignedFrom || assignedTo || completedFrom || completedTo;

  const clearAllFilters = () => {
    setUserFilter(''); setOtaStatusFilter(''); setOtaPlatformFilter('');
    setCompletenessFilter(''); setTimePreset('all'); setDeadlineStatusFilter('');
    setAssignedFrom(''); setAssignedTo(''); setCompletedFrom(''); setCompletedTo('');
    setPage(1);
    clearPageFilters(user?.id, 'cx_dashboard');
  };

  return (
    <div className="space-y-5" data-testid="cx-leader-dashboard">
      {/* CX Leader Approval Insights - TOP */}
      <CXLeaderApprovalInsights />

      {/* Global Filter Bar - TOP */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm sticky top-0 z-10">
        <div className="px-5 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
              {TIME_PRESETS.map(p => (
                <button key={p.key} onClick={() => { setTimePreset(p.key); setPage(1); }}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${timePreset === p.key ? 'bg-white text-teal-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                  data-testid={`cx-time-${p.key}`}>{p.label}</button>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium border transition-all ${showFilters || hasActiveFilters ? 'bg-teal-50 border-teal-200 text-teal-700' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}
              data-testid="cx-toggle-filters">
              <Filter size={14} /> Filters {hasActiveFilters && <span className="w-2 h-2 bg-teal-500 rounded-full" />}
            </button>
            <button onClick={loadData} className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50" data-testid="cx-refresh">
              <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            </button>
          </div>
        </div>
        {timePreset === 'custom' && (
          <div className="px-5 pb-3 flex items-center gap-2">
            <span className="text-xs text-gray-500 font-medium">Lead Date:</span>
            <input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)} className="px-2 py-1.5 border border-gray-200 rounded-lg text-xs" data-testid="cx-custom-from" />
            <span className="text-xs text-gray-400">to</span>
            <input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)} className="px-2 py-1.5 border border-gray-200 rounded-lg text-xs" data-testid="cx-custom-to" />
          </div>
        )}
        {showFilters && (
          <div className="px-5 py-3 border-t border-gray-100 bg-gray-50/50 space-y-3" data-testid="cx-filter-panel">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Assigned User</label>
                <select value={userFilter} onChange={e => { setUserFilter(e.target.value); setPage(1); }}
                  className="w-full px-2 py-2 border border-gray-200 rounded-lg text-sm bg-white" data-testid="cx-user-filter">
                  <option value="">All Users</option>
                  {users.map(u => <option key={u.email} value={u.name || u.email}>{u.name || u.email}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">OTA Status</label>
                <select value={otaStatusFilter} onChange={e => { setOtaStatusFilter(e.target.value); setPage(1); }}
                  className="w-full px-2 py-2 border border-gray-200 rounded-lg text-sm bg-white" data-testid="cx-ota-status-filter">
                  <option value="">All Statuses</option>
                  <option value="pending_assignment">Needs Assignment</option>
                  <option value="assigned">Assigned</option>
                  <option value="in_progress">In Progress</option>
                  <option value="completed">Completed</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">OTA Platform</label>
                <select value={otaPlatformFilter} onChange={e => { setOtaPlatformFilter(e.target.value); setPage(1); }}
                  className="w-full px-2 py-2 border border-gray-200 rounded-lg text-sm bg-white" data-testid="cx-ota-platform-filter">
                  <option value="">All Platforms</option>
                  {OTA_PLATFORMS.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Completeness</label>
                <select value={completenessFilter} onChange={e => { setCompletenessFilter(e.target.value); setPage(1); }}
                  className="w-full px-2 py-2 border border-gray-200 rounded-lg text-sm bg-white" data-testid="cx-completeness-filter">
                  <option value="">All</option>
                  <option value="incomplete">Incomplete (Missing ID/URL)</option>
                  <option value="complete">Complete</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">SLA Status</label>
                <select value={deadlineStatusFilter} onChange={e => { setDeadlineStatusFilter(e.target.value); setPage(1); }}
                  className="w-full px-2 py-2 border border-gray-200 rounded-lg text-sm bg-white" data-testid="cx-deadline-status-filter">
                  <option value="">All</option>
                  <option value="on_time">On Time</option>
                  <option value="late">Late</option>
                  <option value="overdue">Overdue</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Assigned From</label>
                <input type="date" value={assignedFrom} onChange={e => { setAssignedFrom(e.target.value); setPage(1); }}
                  className="w-full px-2 py-2 border border-gray-200 rounded-lg text-sm" data-testid="cx-assigned-from" />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Assigned To</label>
                <input type="date" value={assignedTo} onChange={e => { setAssignedTo(e.target.value); setPage(1); }}
                  className="w-full px-2 py-2 border border-gray-200 rounded-lg text-sm" data-testid="cx-assigned-to" />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Completed From</label>
                <input type="date" value={completedFrom} onChange={e => { setCompletedFrom(e.target.value); setPage(1); }}
                  className="w-full px-2 py-2 border border-gray-200 rounded-lg text-sm" data-testid="cx-completed-from" />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Completed To</label>
                <input type="date" value={completedTo} onChange={e => { setCompletedTo(e.target.value); setPage(1); }}
                  className="w-full px-2 py-2 border border-gray-200 rounded-lg text-sm" data-testid="cx-completed-to" />
              </div>
            </div>
            {hasActiveFilters && (
              <button onClick={clearAllFilters}
                className="text-xs text-teal-600 hover:text-teal-800 font-medium flex items-center gap-1" data-testid="cx-clear-filters"><X size={12} /> Clear all</button>
            )}
          </div>
        )}
      </div>

      {/* Primary KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
        {[
          { label: 'Closed Won', value: a.total_closed_won || 0, sub: `${a.new_closed_won_7d || 0} new this week`, color: 'emerald', icon: CheckCircle },
          { label: 'Total OTAs', value: a.total_otas || 0, color: 'teal', icon: Globe2 },
          { label: 'Pending Assignment', value: a.pending_assignment || 0, color: 'red', icon: AlertTriangle },
          { label: 'Assigned', value: a.assigned_otas || 0, color: 'blue', icon: User },
          { label: 'Integration', value: a.integration_count || 0, color: 'amber', icon: Clock },
          { label: 'Completed', value: a.completed_otas || 0, color: 'emerald', icon: CheckCircle },
        ].map(k => (
          <div key={k.label} className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm" data-testid={`cx-kpi-${k.label.toLowerCase().replace(/\s+/g, '-')}`}>
            <div className="flex items-center justify-between mb-2">
              <div className={`w-9 h-9 rounded-lg bg-${k.color}-50 flex items-center justify-center`}>
                <k.icon size={18} className={`text-${k.color}-500`} />
              </div>
            </div>
            <p className="text-2xl font-bold text-gray-900">{k.value}</p>
            <p className="text-xs text-gray-500">{k.label}</p>
            {k.sub && <p className="text-[10px] text-teal-600 mt-0.5">{k.sub}</p>}
          </div>
        ))}
      </div>

      {/* SLA KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm" data-testid="cx-kpi-late">
          <div className="flex items-center justify-between mb-2">
            <div className="w-9 h-9 rounded-lg bg-red-50 flex items-center justify-center"><ShieldAlert size={18} className="text-red-500" /></div>
          </div>
          <p className="text-2xl font-bold text-gray-900">{a.late_count || 0}</p>
          <p className="text-xs text-gray-500">Late Completions</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm" data-testid="cx-kpi-on-time-pct">
          <div className="flex items-center justify-between mb-2">
            <div className="w-9 h-9 rounded-lg bg-emerald-50 flex items-center justify-center"><TrendingUp size={18} className="text-emerald-500" /></div>
          </div>
          <p className="text-2xl font-bold text-gray-900">{a.on_time_pct || 0}%</p>
          <p className="text-xs text-gray-500">On-Time Rate</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm" data-testid="cx-kpi-overdue">
          <div className="flex items-center justify-between mb-2">
            <div className="w-9 h-9 rounded-lg bg-orange-50 flex items-center justify-center"><AlertTriangle size={18} className="text-orange-500" /></div>
          </div>
          <p className="text-2xl font-bold text-gray-900">{a.overdue_count || 0}</p>
          <p className="text-xs text-gray-500">Currently Overdue</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm" data-testid="cx-kpi-avg-time">
          <div className="flex items-center justify-between mb-2">
            <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center"><Timer size={18} className="text-blue-500" /></div>
          </div>
          <p className="text-2xl font-bold text-gray-900">{a.avg_completion_hours != null ? `${a.avg_completion_hours}h` : '-'}</p>
          <p className="text-xs text-gray-500">Avg Completion Time</p>
        </div>
      </div>

      {/* Leads Table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        {/* Table Header: Info | Search | Pagination */}
        <div className="px-4 py-3 border-b border-gray-100">
          <div className="flex flex-col sm:flex-row items-center gap-3">
            <div className="hidden sm:flex items-center gap-2 text-xs text-gray-500 shrink-0 min-w-[180px]">
              <span>Showing {leads.length} of {totalRecords}</span>
              <span className="text-gray-300">|</span>
              <span>Page {page}/{totalPages}</span>
            </div>
            <div className="w-full sm:flex-1 sm:max-w-sm mx-auto">
              <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input type="text" value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
                  placeholder="Search leads..." className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:ring-1 focus:ring-teal-400 outline-none" data-testid="cx-search" />
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <div className="flex items-center gap-1.5 text-xs text-gray-500">
                <span className="hidden sm:inline">Per page:</span>
                <select value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}
                  className="px-2 py-1.5 border border-gray-200 rounded-lg bg-white text-xs" data-testid="cx-page-size">
                  {[10, 25, 50, 100].map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="flex gap-1.5">
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}
                  className="px-3 py-1.5 border border-gray-200 rounded-lg disabled:opacity-40 hover:bg-gray-50 text-xs" data-testid="cx-prev-page">Prev</button>
                <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}
                  className="px-3 py-1.5 border border-gray-200 rounded-lg disabled:opacity-40 hover:bg-gray-50 text-xs" data-testid="cx-next-page">Next</button>
              </div>
            </div>
          </div>
          <div className="sm:hidden flex items-center justify-between text-xs text-gray-500 mt-2">
            <span>Showing {leads.length} of {totalRecords}</span>
            <span>Page {page}/{totalPages}</span>
          </div>
        </div>
        {loading ? (
          <div className="flex items-center justify-center h-40"><Loader2 size={28} className="animate-spin text-teal-500" /></div>
        ) : leads.length > 0 ? (
          <>
            <div className="overflow-x-auto" data-testid="cx-leads-table">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500">Hotel Name</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500">Lead Date</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500">Owner</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500">OTAs</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500">Done</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500">Pending</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500">Integration</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500">SLA</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {leads.map(l => {
                    const lateOtas = (l.ota_info || []).filter(o => o.sla_status === 'late' || o.sla_status === 'overdue').length;
                    const onTimeOtas = (l.ota_info || []).filter(o => o.sla_status === 'on_time').length;
                    return (
                      <tr key={l.id} className="hover:bg-gray-50 cursor-pointer transition-colors" onClick={() => setSelectedLead(l)} data-testid={`cx-lead-row-${l.id}`}>
                        <td className="px-4 py-3 font-semibold text-gray-800">{l.hotel_name}</td>
                        <td className="px-4 py-3 text-gray-600 text-xs">{l.lead_date}</td>
                        <td className="px-4 py-3 text-gray-600">{l.lead_owner}</td>
                        <td className="px-4 py-3 text-center font-medium">{l.ota_count}</td>
                        <td className="px-4 py-3 text-center"><span className="text-emerald-600 font-medium">{l.ota_completed}</span></td>
                        <td className="px-4 py-3 text-center">{l.ota_pending > 0 ? <span className="text-red-600 font-medium">{l.ota_pending}</span> : <span className="text-gray-400">0</span>}</td>
                        <td className="px-4 py-3 text-center" data-testid={`cx-integration-count-${l.id}`}>{(l.integration_count || 0) > 0 ? <span className="text-amber-600 font-medium">{l.integration_count}</span> : <span className="text-gray-400">0</span>}</td>
                        <td className="px-4 py-3 text-center">
                          <div className="flex items-center justify-center gap-1">
                            {onTimeOtas > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 font-medium">{onTimeOtas}</span>}
                            {lateOtas > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-100 text-red-700 font-medium">{lateOtas}</span>}
                            {onTimeOtas === 0 && lateOtas === 0 && <span className="text-gray-400 text-xs">-</span>}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <button className="p-1.5 text-gray-400 hover:text-teal-600 hover:bg-teal-50 rounded-lg" data-testid={`cx-open-lead-${l.id}`}><ChevronRight size={16} /></button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        ) : (
          <div className="py-16 text-center">
            <CheckCircle size={40} className="mx-auto mb-3 text-gray-300" />
            <h3 className="text-base font-semibold text-gray-800 mb-1">No Closed Won Leads</h3>
            <p className="text-sm text-gray-500">{hasActiveFilters ? 'Try adjusting your filters' : 'No closed won leads found'}</p>
          </div>
        )}
      </div>

      {/* Drawer */}
      <OTADrawer lead={selectedLead} onClose={() => setSelectedLead(null)} onAssign={handleAssign} onComplete={handleComplete} users={users} />
    </div>
  );
};

export default CXLeaderDashboard;
